import { Box, Grid, SxProps } from '@mui/material';
import { getRequestAnalysts } from 'lib/api/financing';
import { hasAnyPermissions } from 'lib/functions/utils';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import AnalystRow from './components/AnalystRow';

const ALL_ANALYST_PERMISSIONS = [
  'businesses_read_all',
  'assign_factoring_risk_analyst',
  'be_factoring_risk_analyst',
  'assign_confirming_risk_analyst',
  'be_confirming_risk_analyst',
  'assign_credit_risk_analyst',
  'be_credit_risk_analyst',
  'assign_factoring_operations_analyst',
  'be_factoring_operations_analyst',
  'assign_confirming_operations_analyst',
  'be_confirming_operations_analyst',
  'assign_credit_operations_analyst',
  'be_credit_operations_analyst',
];

const PERMISSIONS_FOR_SEEING_ANALYSTS = {
  'factoring::risk': ['be_factoring_risk_analyst', 'assign_factoring_risk_analyst'],
  'factoring::operations': ['be_factoring_operations_analyst', 'assign_factoring_operations_analyst'],
  'confirming::risk': ['be_confirming_risk_analyst', 'assign_confirming_risk_analyst'],
  'confirming::operations': ['be_confirming_operations_analyst', 'assign_confirming_operations_analyst'],
  'credit::risk': ['be_credit_request_risk_analyst', 'assign_credit_risk_analyst'],
  'credit::operations': ['be_credit_request_operations_analyst', 'assign_credit_operations_analyst'],
};

type RequestAnalystsProps = {
  requestId: number;
  requestType: 'factoring' | 'confirming' | 'credit';
  sx?: SxProps;
  hideOperations?: boolean;
};

const RequestAnalysts = (props: RequestAnalystsProps) => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const { requestId, requestType, sx, hideOperations } = props;
  const fetchAnalysts = hasAnyPermissions(user, ALL_ANALYST_PERMISSIONS);
  const { data, isLoading } = getRequestAnalysts(dispatch, requestId, requestType, fetchAnalysts);

  if (isLoading || !fetchAnalysts || !data) {
    return null;
  }

  const canSeeRiskAnalyst = hasAnyPermissions(user, PERMISSIONS_FOR_SEEING_ANALYSTS[`${requestType}::risk`]);
  const canSeeOperationsAnalyst = hasAnyPermissions(user, PERMISSIONS_FOR_SEEING_ANALYSTS[`${requestType}::operations`]);

  const canSeeBoth = canSeeRiskAnalyst && canSeeOperationsAnalyst;

  return (
    <Box sx={sx}>
      <Grid container spacing={2}>
        {canSeeRiskAnalyst && (
          <Grid item xs={canSeeBoth ? 6 : 12}>
            <AnalystRow
              executive={data.riskAnalyst}
              analystType="risk"
              requestId={requestId}
              requestType={requestType}
              otherAnalystId={data.operationsAnalyst?.id || null}
            />
          </Grid>
        )}
        {canSeeOperationsAnalyst && !hideOperations && (
          <Grid item xs={canSeeBoth ? 6 : 12}>
            <AnalystRow
              executive={data.operationsAnalyst}
              analystType="operations"
              requestId={requestId}
              requestType={requestType}
              otherAnalystId={data.riskAnalyst?.id || null}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default RequestAnalysts;